/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { DataGrid, EditModal } from "../../../common";
import CustomSelect from "../../../common/CustomSelect";
import { FilterBodyTemplate } from "../../../utils/configs";
import { APIEndpoint, DFPAPIEndpointGalley } from "../../../utils/axios";
import catererdata from "../../../assets/json/caterer_inv.json";
import { AiFillQuestionCircle } from "react-icons/ai";
import {
  CateringStatusRenderSel,
} from "../../sales/inner-components/renderers";
import viewIcon from "../../../assets/view.svg";
import cancelIcon from "../../../assets/icons/cancel.svg";
import confirmIcon from "../../../assets/icons/confirm.svg";
import searchIcon from "../../../assets/icons/search.svg";
import usePermissions from "../../../utils/userPermissions";
import { CreatecatererInvoice } from "./create-caterer-invoice";
import { CatererInvoiceReviewModal } from "./caterer-invoice-review-modal";
import { IS_DEMO_MODE, loadPersistedDemoInvoices, persistDemoInvoices, formatDemoCurrency } from "../../../assets/demo/demoInvoiceData";

export const CatererInvoicePage = (props) => {
  const { t, ready } = useTranslation();
  const [state, setState] = useState({});
  const [perPageLimit] = useState(50);
  const [EditFieldsCess, setEditFieldsCess] = useState([]);
  const [isReloadTableData, setIsReloadTableData] = useState(false);
  const [modalTitle, setModalTitle] = useState("Order Details");
  const [modalCustomButtons, setModalCustomButtons] = useState(null);
  const [modalFormStatusObj] = useState({ text: '' });
  const [caterer, setCaterer] = useState([]);
  const [searchKey, setSearchKey] = useState("");

  // Live state holding invoice records (initialized with rich mock data)
  const [catererInvoiceList, setCatererInvoiceList] = useState(catererdata);

  // Invoices created through the frontend-only demo simulation — kept apart
  // from catererInvoiceList since they were never actually submitted to a
  // real backend (see fetchOrders below and demoInvoiceData.js). Persisted to
  // localStorage so a page refresh mid-demo doesn't lose them.
  const [demoInvoices, setDemoInvoices] = useState(() => loadPersistedDemoInvoices());

  useEffect(() => {
    persistDemoInvoices(demoInvoices);
  }, [demoInvoices]);

  const { hasPermission: createcatInv } = usePermissions('create', "procurement.caterer-invoice");
  const { hasPermission: editcatInv } = usePermissions('edit', "procurement.caterer-invoice");
  const { hasPermission: viewDetailscatInv } = usePermissions('viewDetails', "procurement.caterer-invoice");
  const { hasPermission: approvecatInv } = usePermissions('approve', "procurement.caterer-invoice");

  const [salesFilter, setSalesFilter] = useState({
    catererVal: "",
    invoiceTypeVal: "",
    orderStatusVal: "",
    searchKey: "",
  });

  const invoiceTypeFilterOptions = [
    { key: 0, label: "All Invoice Types", value: "" },
    { key: 1, label: "Service", value: "Service" },
    { key: 2, label: "Uplifts", value: "Uplifts" },
    { key: 3, label: "Bulk Deliveries", value: "Bulk Deliveries" }
  ];

  const statusFilterOptions = [
    { key: 0, label: "All Statuses", value: "" },
    { key: 1, label: "Under Review", value: "Under Review" },
    { key: 2, label: "Clarification", value: "Clarification" },
    { key: 3, label: "Accepted", value: "Accepted" },
    { key: 4, label: "Rejected", value: "Rejected" },
    { key: 5, label: "Processing", value: "Processing" }
  ];

  useEffect(() => {
    loadCaterers();
    // Also recompute whenever the underlying invoice lists change (e.g. a
    // freshly-submitted demo invoice introduces a caterer not seen before),
    // so the dropdown always reflects what's actually on the page.
  }, [salesFilter, catererInvoiceList, demoInvoices]);

  // DataGrid (ag-grid infinite row model) only re-fetches when its
  // `resetDataSource` prop toggles — changing the dropdown filters or typing
  // in the search box alone doesn't tell it anything changed, so without
  // this the grid keeps showing stale results.
  useEffect(() => {
    setIsReloadTableData(prev => !prev);
  }, [salesFilter, searchKey]);

  const loadCaterers = async () => {
    if (IS_DEMO_MODE) {
      // Derived from what's actually listed on the page, not a hardcoded
      // vendor list — so the filter never offers a caterer with no rows, and
      // never omits one that does have rows.
      const uniqueCatererNames = Array.from(
        new Set([...catererInvoiceList, ...demoInvoices].map(inv => inv.catererName).filter(Boolean))
      ).sort((a, b) => a.localeCompare(b));
      setCaterer([
        { key: 0, label: "All Caterers", value: "" },
        ...uniqueCatererNames.map((name, idx) => ({ key: idx + 1, label: name, value: name }))
      ]);
      return;
    }

    const filterData = { ...FilterBodyTemplate, filters: [] };
    filterData.page = 1;
    filterData.limit = 10000;
    try {
      await DFPAPIEndpointGalley.post('galley/caterers/process', JSON.stringify(filterData))
        .then(resp => {
          const options = [{ key: 0, label: "All Caterers", value: "" }];
          if (resp.items) {
            resp.items.map(item => (options.push({ key: item.id, label: `${item.code}-${item.name2}`, value: `${item.name2}` })));
          }
          setCaterer(options);
        }).catch(error => {
          console.log('Log: Error fetching caterer list:', error.message);
          setCaterer([{ key: 0, label: "All Caterers", value: "" }]);
        });
    } catch (error) {}
  };

  // Invoices created via the frontend-only demo simulation (see
  // demoInvoiceData.js) live in their own state, separate from whatever the
  // real backend returns — they were never actually persisted server-side,
  // so a successful API fetch must not be allowed to silently wipe them out.
  const matchesCurrentFilters = useCallback((ct) => {
    if (salesFilter.catererVal && !ct.catererName?.toLowerCase().includes(salesFilter.catererVal.toLowerCase())) return false;
    if (salesFilter.invoiceTypeVal && ct.invoiceType !== salesFilter.invoiceTypeVal) return false;
    if (salesFilter.orderStatusVal && ct.status?.toLowerCase() !== salesFilter.orderStatusVal.toLowerCase()) return false;
    if (searchKey) {
      const sk = searchKey.toLowerCase();
      const isMatch =
        ct.invoiceId?.toLowerCase().includes(sk) ||
        ct.catererName?.toLowerCase().includes(sk) ||
        (ct.poNumber && ct.poNumber.toLowerCase().includes(sk)) ||
        (ct.flightNumber && ct.flightNumber.toLowerCase().includes(sk)) ||
        (ct.locationCode && ct.locationCode.toLowerCase().includes(sk));
      if (!isMatch) return false;
    }
    return true;
  }, [salesFilter, searchKey]);

  // Fetch orders with multi-criteria filtering from backend API
  const fetchOrders = useCallback(
    async (pageObj) => {
      const matchingDemoInvoices = demoInvoices.filter(matchesCurrentFilters);

      if (!IS_DEMO_MODE) {
        try {
          const payload = {
            page: pageObj?.page || 1,
            limit: pageObj?.limit || perPageLimit,
            catererName: salesFilter.catererVal,
            invoiceType: salesFilter.invoiceTypeVal,
            status: salesFilter.orderStatusVal,
            searchKey: searchKey
          };
          const response = await APIEndpoint.post('caterer-invoices/process', payload);
          if (response && Array.isArray(response.items)) {
            setCatererInvoiceList(response.items);
            const mergedItems = [...matchingDemoInvoices, ...response.items];
            const baseCount = response.count !== undefined ? response.count : response.items.length;
            return { count: baseCount + matchingDemoInvoices.length, items: mergedItems };
          }
        } catch (error) {
          console.warn('API fetch caterer invoices failed, falling back to local list:', error.message);
        }
      }

      // Demo mode / offline fallback: purely local filtering, no network call.
      let filtered = [...catererInvoiceList].filter(matchesCurrentFilters);
      const mergedItems = [...matchingDemoInvoices, ...filtered];

      return { count: mergedItems.length, items: mergedItems };
    },
    [searchKey, salesFilter, perPageLimit, catererInvoiceList, demoInvoices, matchesCurrentFilters]
  );

  const handleAction = (type, data) => {
    let updatedEditFields = [];
    if (type === 'view' && viewDetailscatInv !== false) {
      setState((_prev) => {
        return { ..._prev, selected: { items: [data], catData: data, action: type } };
      });
    } else if (type === 'accept' && approvecatInv !== false) {
      setModalTitle('Accept Invoice - ' + data.locationCode + " (" + data.invoiceId + ")");
      updatedEditFields = [
        {
          label: `Are you sure you want to approve invoice ${data.invoiceId} for ${formatDemoCurrency(data.total)}? This will confirm operational sign-off and unlock it for finance payment.`,
          attribute: 'alertMessage', 
          type: 'alertMessage'
        }
      ];
      setModalCustomButtons([
        {
          label: 'Confirm Approval', 
          attribute: 'button', 
          type: "button", 
          disabled: false, 
          className: 'submit-button', 
          action: () => {
            handleInvoiceActionUpdate(data.id, 'Accepted');
            handleModalCancel();
          }
        }
      ]);
      setState((_prev) => {
        return { ..._prev, selected: { data, action: type } };
      });
      setEditFieldsCess(updatedEditFields);
    } else if (type === 'reject' && editcatInv !== false) {
      setModalTitle('Reject Invoice - ' + data.locationCode + " (" + data.invoiceId + ")");
      updatedEditFields = [
        {
          label: `Are you sure you want to reject invoice ${data.invoiceId}?`,
          attribute: 'alertMessage', 
          type: 'alertMessage'
        }
      ];
      setModalCustomButtons([
        {
          label: 'Confirm Rejection', 
          attribute: 'button', 
          type: "button", 
          disabled: false, 
          className: 'submit-button', 
          action: () => {
            handleInvoiceActionUpdate(data.id, 'Rejected');
            handleModalCancel();
          }
        }
      ]);
      setState((_prev) => {
        return { ..._prev, selected: { data, action: type } };
      });
      setEditFieldsCess(updatedEditFields);
    } else if (type === 'clarfy') {
      setModalTitle('Clarification Required - ' + data.locationCode + " (" + data.invoiceId + ")");
      updatedEditFields = [
        {
          label: `You are about to mark invoice ${data.invoiceId} as Clarification Required.`,
          attribute: 'alertMessage', 
          type: 'alertMessage'
        }
      ];
      setModalCustomButtons([
        {
          label: 'Send Clarification Request', 
          attribute: 'button', 
          type: "button", 
          disabled: false, 
          className: 'submit-button', 
          action: () => {
            handleInvoiceActionUpdate(data.id, 'Clarification');
            handleModalCancel();
          }
        }
      ]);
      setState((_prev) => {
        return { ..._prev, selected: { data, action: type } };
      });
      setEditFieldsCess(updatedEditFields);
    }
  };

  const handleInvoiceActionUpdate = async (id, newStatus, meta = {}) => {
    // isDemo decides which local state bucket to update; IS_DEMO_MODE decides
    // whether we're allowed to touch the network at all — while demo mode is
    // on, nothing here is backend-backed, so skip the call unconditionally.
    const isDemo = String(id).startsWith('DEMO-');
    if (!IS_DEMO_MODE && !isDemo) {
      try {
        await APIEndpoint.put(`caterer-invoices/${id}/status`, {
          status: newStatus,
          note: meta.note || `Invoice marked as ${newStatus}`
        });
      } catch (err) {
        console.warn('API update invoice status failed, updating local state:', err.message);
      }
    }
    const applyStatusUpdate = prev => prev.map(item => item.id === id ? { ...item, status: newStatus, ...meta } : item);
    if (isDemo) {
      setDemoInvoices(applyStatusUpdate);
    } else {
      setCatererInvoiceList(applyStatusUpdate);
    }
    setIsReloadTableData(prev => !prev);
  };

  function actionSets(props) {
    if (!props.data) return <div />;
    const { status } = props.data;
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span title="Review Details" className="pointer" onClick={() => handleAction("view", props.data)}>
          <img className="aircraft-actions" alt="view" src={viewIcon} />
        </span>
        {status !== 'Accepted' && (
          <span title="Accept Invoice" className="pointer" onClick={() => handleAction("accept", props.data)}>
            <img className="aircraft-actions" alt="accept" src={confirmIcon} />
          </span>
        )}
        {status !== 'Rejected' && (
          <span title="Reject Invoice" className="pointer" onClick={() => handleAction("reject", props.data)}>
            <img className="aircraft-actions" alt="reject" src={cancelIcon} />
          </span>
        )}
        {status !== 'Clarification' && status !== 'Accepted' && (
          <span title="Request Clarification" className="pointer" onClick={() => handleAction("clarfy", props.data)}>
            <AiFillQuestionCircle size={18} color="#fc7f03" className="aircraft-actions" />
          </span>
        )}
      </div>
    );
  }

  const getAgeFormatted = (data) => {
    if (!data) return 'Today';
    if (data >= 30) {
      const months = Math.round(data / 30);
      return `${months} month${months > 1 ? 's' : ''}`;
    }
    return `${data} day${data > 1 ? 's' : ''}`;
  };

  const InvoiceTypeRenderer = (params) => {
    if (!params.data || !params.data.invoiceType) return null;
    const type = params.data.invoiceType;
    let bg = 'rgba(156, 39, 176, 0.2)';
    let color = '#ba68c8';
    let border = '1px solid #ba68c8';
    if (type === 'Uplifts') {
      bg = 'rgba(0, 188, 212, 0.2)';
      color = '#4dd0e1';
      border = '1px solid #4dd0e1';
    } else if (type === 'Bulk Deliveries') {
      bg = 'rgba(33, 150, 243, 0.2)';
      color = '#64b5f6';
      border = '1px solid #64b5f6';
    }
    return (
      <span style={{ background: bg, color: color, border: border, padding: '2px 10px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: 600 }}>
        {type}
      </span>
    );
  };

  const AmountRenderer = (params) => {
    if (!params.data) return null;
    return (
      <span style={{ fontWeight: 600, color: '#fff' }}>
        {formatDemoCurrency(params.data.total)}
      </span>
    );
  };

  const VarianceRenderer = (params) => {
    if (!params.data) return null;
    const variance = params.data.variance || 0;
    if (variance > 0) {
      return (
        <span style={{ color: '#ff9800', fontWeight: 600, background: 'rgba(255, 152, 0, 0.15)', padding: '2px 8px', borderRadius: '4px' }}>
          +{formatDemoCurrency(variance)}
        </span>
      );
    }
    return (
      <span style={{ color: '#4caf50', fontWeight: 600 }}>
        {formatDemoCurrency(0)}
      </span>
    );
  };

  const defaultCellStyle = {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: 'flex',
    alignItems: 'center'
  };

  const Columns = [
    {
      field: "invoiceId",
      headerName: "Invoice ID",
      sortable: false,
      filter: false,
      width: 210,
      minWidth: 190,
      pinned: "left",
      cellStyle: defaultCellStyle,
      cellRenderer: (params) => {
        return (
          <span 
            className="pointer" 
            style={{ color: '#3dabf5', fontWeight: 600, cursor: 'pointer', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} 
            onClick={() => handleAction("view", params.data)}
            title={params.data?.invoiceId}
          >
            {params.data?.invoiceId}
          </span>
        );
      }
    },
    {
      field: "invoiceType",
      headerName: "Type",
      width: 140,
      minWidth: 130,
      cellStyle: defaultCellStyle,
      cellRendererSelector: () => ({ component: InvoiceTypeRenderer })
    },
    {
      field: "locationCode",
      headerName: "Station",
      width: 190,
      minWidth: 170,
      tooltipField: 'locationCode',
      cellStyle: defaultCellStyle
    },
    {
      field: "catererName",
      headerName: "Caterer",
      sortable: false,
      filter: false,
      width: 280,
      minWidth: 240,
      tooltipField: 'catererName',
      cellStyle: defaultCellStyle,
    },
    {
      field: "period",
      headerName: "Period / PO Ref",
      width: 230,
      minWidth: 200,
      tooltipField: 'period',
      cellStyle: defaultCellStyle,
      valueGetter: (params) => {
        return params.data?.period || params.data?.poNumber || params.data?.flightNumber || 'N/A';
      }
    },
    {
      field: "total",
      headerName: "Invoice Total",
      width: 140,
      minWidth: 130,
      cellStyle: defaultCellStyle,
      cellRendererSelector: () => ({ component: AmountRenderer })
    },
    {
      field: "variance",
      headerName: "Variance",
      width: 130,
      minWidth: 120,
      cellStyle: defaultCellStyle,
      cellRendererSelector: () => ({ component: VarianceRenderer })
    },
    {
      field: "age",
      headerName: "Age",
      sortable: false,
      filter: false,
      width: 110,
      minWidth: 100,
      cellStyle: defaultCellStyle,
      valueGetter: (params) => {
        if (params.data && params.data.age) {
          return getAgeFormatted(params.data.age);
        }
        return '1 day';
      }
    },
    {
      field: "status",
      headerName: "Status",
      sortable: false,
      filter: false,
      width: 160,
      minWidth: 150,
      cellStyle: defaultCellStyle,
      cellRendererSelector: () => ({ component: CateringStatusRenderSel })
    },
    {
      headerName: 'Actions',
      pinned: 'right',
      width: 150,
      minWidth: 140,
      resizable: false,
      suppressSizeToFit: true,
      cellRenderer: actionSets,
      headerClass: 'actions-header',
    }
  ];

  const handleModalCancel = () => {
    setState((prev) => ({ ...prev, selected: false }));
  };

  const onClose = () => {
    setState((_prev) => ({ ..._prev, selected: false }));
  };

  const onAddInvoice = () => {
    setState((prev) => ({ ...prev, selected: { action: "addInvoice" } }));
  };

  return (
    <div className="op-aircraft-container flexCol full-flex ag-theme-alpine-dark">
      {/* FILTER CONTROLS HEADER */}
      <div className="flexRow width100 margBot10 justifyContentSpaceBetween" style={{ flexWrap: 'wrap', gap: '8px' }}>
        <div className="flexRow alignItemsCenter" style={{ flexWrap: 'wrap', gap: '8px' }}>
          
          {/* Caterer Selector */}
          <div className='saleType-div'>
            <CustomSelect
              options={caterer}
              placeHolder="All Caterers"
              onChange={(e) => setSalesFilter({ ...salesFilter, catererVal: e?.value ?? "" })}
            />
          </div>

          {/* Invoice Type Selector */}
          <div className='saleType-div'>
            <CustomSelect
              options={invoiceTypeFilterOptions}
              placeHolder="All Invoice Types"
              onChange={(e) => setSalesFilter({ ...salesFilter, invoiceTypeVal: e?.value ?? "" })}
            />
          </div>

          {/* Status Selector */}
          <div className='saleType-div'>
            <CustomSelect
              options={statusFilterOptions}
              placeHolder="All Statuses"
              onChange={(e) => setSalesFilter({ ...salesFilter, orderStatusVal: e?.value ?? "" })}
            />
          </div>

          {/* Search Bar */}
          <div className="underline" style={{ marginLeft: '6px' }}>
            <span>
              <img className="icon-size" alt={t("filters.searchText")} src={searchIcon} />
            </span>
            <span>
              <input
                className="search"
                onChange={(e) => setSearchKey(e.target.value)}
                type="search"
                placeholder="Search Invoice #, Caterer, Station, Flight, PO..."
                value={searchKey}
                style={{ width: '220px' }}
              />
            </span>
          </div>
        </div>

        {/* Add Invoice CTA */}
        {createcatInv !== false && (
          <div className="flexRow alignItemsCenter">
            <button 
              className="add-item-button" 
              onClick={onAddInvoice}
              style={{
                backgroundColor: '#3dabf5',
                padding: '8px 18px',
                borderRadius: '4px',
                color: '#fff',
                fontWeight: 600,
                fontSize: '0.85rem',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                cursor: 'pointer',
                boxShadow: '0 2px 6px rgba(61, 171, 245, 0.4)'
              }}
            >
              + Upload Invoice
            </button>
          </div>
        )}
      </div>

      {/* DATA GRID */}
      <DataGrid
        className="full-flex"
        columns={[...Columns]}
        initialPageLimit={perPageLimit}
        showDefaultFilters={false}
        searchKey={searchKey}
        getRows={fetchOrders}
        rowSelection="single"
        columnResize={false}
        resetDataSource={isReloadTableData}
      />

      {/* DYNAMIC UPLOAD MODAL */}
      {state && Boolean(state.selected) && state.selected?.action === "addInvoice" && (
        <CreatecatererInvoice
          key={state.selected?.action}
          onClose={onClose}
          onSubmit={handleModalCancel}
          existingDemoInvoices={demoInvoices}
          onInvoiceCreated={(newInv) => {
            if (String(newInv.id).startsWith('DEMO-')) {
              setDemoInvoices(prev => [newInv, ...prev]);
            } else {
              setCatererInvoiceList(prev => [newInv, ...prev]);
            }
            setIsReloadTableData(prev => !prev);
            handleModalCancel();
          }}
        />
      )}

      {/* SPLIT-SCREEN REVIEW MODAL */}
      {state && Boolean(state.selected) && state.selected?.action === "view" && (
        <CatererInvoiceReviewModal
          key={state.selected?.action}
          invoice={state.selected?.catData || state.selected}
          onClose={onClose}
          onActionUpdate={(id, newStatus, meta) => {
            handleInvoiceActionUpdate(id, newStatus, meta);
          }}
        />
      )}

      {/* QUICK CONFIRMATION ACTION MODAL */}
      {(state?.selected?.action === 'accept' || state?.selected?.action === 'reject' || state?.selected?.action === 'clarfy') && Boolean(state?.selected) ? (
        <EditModal
          key={state.selected.action}
          title={modalTitle}
          onClose={handleModalCancel}
          mainContentCenter={true}
          modalFormStatusObj={modalFormStatusObj}
          onSubmit={handleModalCancel}
          modalCustomButtons={modalCustomButtons}
          fields={EditFieldsCess.map((elt) => ({ ...elt, initialValue: state.selected.data[elt.attribute] }))}
        />
      ) : null}

      {!ready && <div className='overlay_hide sub'></div>}
    </div>
  );
};